/**
 * Prepare a chosen photo for attaching (RT-4). Like the avatar editor, the
 * browser decodes the file and re-encodes it as JPEG on a canvas, so what the
 * server receives never depends on `File.type` — which comes from the file
 * extension, is empty on some platforms, and may name a format (HEIC) the
 * server and other browsers cannot use. Scaling the long edge down also keeps
 * a full-size phone photo well under the API's JSON body limit.
 */

/** Long-edge cap: plenty for reading a letter, small enough to post as JSON. */
export const PHOTO_MAX_EDGE = 2048;

export interface EncodedPhoto {
  data: string;
  mime: 'image/jpeg';
  width: number;
  height: number;
}

/** Fit `w × h` inside `max × max`, keeping the aspect ratio; never upscales. */
export function fitWithin(w: number, h: number, max = PHOTO_MAX_EDGE): { width: number; height: number } {
  const scale = Math.min(1, max / Math.max(w, h));
  return { width: Math.max(1, Math.round(w * scale)), height: Math.max(1, Math.round(h * scale)) };
}

export function fileToJpeg(file: File, maxEdge = PHOTO_MAX_EDGE): Promise<EncodedPhoto> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      try {
        const { width, height } = fitWithin(img.naturalWidth, img.naturalHeight, maxEdge);
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('canvas unavailable');
        // JPEG has no alpha: paint white first so transparent PNGs don't go black.
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        const data = canvas.toDataURL('image/jpeg', 0.85).split(',')[1];
        if (!data) throw new Error('could not encode the image');
        resolve({ data, mime: 'image/jpeg', width, height });
      } catch (e) {
        reject(e instanceof Error ? e : new Error('could not read the image'));
      } finally {
        URL.revokeObjectURL(url);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('this browser cannot open that image — try a JPEG or PNG'));
    };
    img.src = url;
  });
}
